import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Event,
  Assignment,
  Quiz,
  Today,
  Add,
  Edit,
  Delete,
  Refresh,
  CalendarMonth,
  AccessTime,
  LocationOn,
  Description
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

import { USER_PROFILE, GOOGLE_CALENDAR_AUTH, GOOGLE_CALENDAR_EVENTS, GOOGLE_CALENDAR_SYNC } from '../../config/Config';

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  useEffect(() => {
    checkConnectionStatus();
    fetchEvents();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      const response = await api.get(USER_PROFILE);
      setIsConnected(response.data.google_calendar_synced || false);
      setLastSync(response.data.google_calendar_last_sync);
    } catch (error) {
      console.error('Error checking calendar connection:', error);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      // Fetch real events from the backend
      const response = await api.get(GOOGLE_CALENDAR_EVENTS);
      const eventsData = response.data.events || [];
      
      // Transform the events to match our frontend format
      const transformedEvents = eventsData.map(event => ({
        id: event.id,
        title: event.title,
        type: event.type,
        date: new Date(event.start_datetime),
        time: event.time,
        end_time: event.end_time,
        duration: event.duration,
        description: event.description,
        location: event.location,
        start_datetime: event.start_datetime,
        end_datetime: event.end_datetime,
        related_id: event.related_id,
        related_type: event.related_type
      }));
      
      setEvents(transformedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      enqueueSnackbar('Failed to fetch events', { variant: 'error' });
      // Fallback to empty events array
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      const response = await api.post(GOOGLE_CALENDAR_SYNC);
      enqueueSnackbar(response.data.message, { variant: 'success' });
      await checkConnectionStatus();
      await fetchEvents();
    } catch (error) {
      console.error('Error syncing with Google Calendar:', error);
      enqueueSnackbar('Failed to sync with Google Calendar', { variant: 'error' });
    }
  };

  const handleConnectGoogleCalendar = async () => {
    try {
      const response = await api.get(GOOGLE_CALENDAR_AUTH);
      const authUrl = response.data?.auth_url || response.data?.url;
      if (!authUrl) {
        enqueueSnackbar('Google Calendar authorization URL was not returned', { variant: 'error' });
        return;
      }
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error connecting to Google Calendar:', error);
      enqueueSnackbar('Failed to connect Google Calendar', { variant: 'error' });
    }
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'test':
        return <Quiz color="primary" />;
      case 'assignment':
        return <Assignment color="secondary" />;
      case 'meeting':
        return <Event color="success" />;
      case 'google_calendar':
        return <CalendarMonth color="info" />;
      default:
        return <Event color="action" />;
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'test':
        return 'primary';
      case 'assignment':
        return 'secondary';
      case 'meeting':
        return 'success';
      case 'google_calendar':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    return time;
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setEventDialogOpen(true);
  };

  const handleCloseEventDialog = () => {
    setEventDialogOpen(false);
    setSelectedEvent(null);
  };

  const getEventsForDate = (date) => {
    const targetDate = new Date(date);
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.toDateString() === targetDate.toDateString();
    });
  };

  const getUpcomingEvents = () => {
    const now = new Date();
    return events
      .filter(event => new Date(event.date) >= now)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchEvents}
            >
              Refresh
            </Button>
            {isConnected && (
              <Button
                variant="contained"
                startIcon={<CalendarMonth />}
                onClick={handleSync}
              >
                Sync with Google Calendar
              </Button>
            )}
          </Box>
        </Box>

        {!isConnected && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Connect your Google Calendar to view your events here. 
            <Button 
              color="inherit" 
              size="small" 
              sx={{ ml: 1 }}
              onClick={handleConnectGoogleCalendar}
            >
              Connect
            </Button>
          </Alert>
        )}

        {isConnected && events.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No events found in your Google Calendar for the next 30 days.
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Today's Events */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Today's Events
                </Typography>
                <List>
                  {getEventsForDate(new Date()).length > 0 ? (
                    getEventsForDate(new Date()).map((event) => (
                      <ListItem 
                        key={event.id} 
                        button 
                        onClick={() => handleEventClick(event)}
                        sx={{ mb: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                      >
                        <ListItemIcon>
                          {getEventIcon(event.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={event.title}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {formatTime(event.time)} • {event.duration}
                              </Typography>
                              <Chip 
                                label={event.type} 
                                size="small" 
                                color={getEventColor(event.type)}
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary="No events today"
                        secondary="You have a free day!"
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Upcoming Events */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Upcoming Events
                </Typography>
                <List>
                  {getUpcomingEvents().length > 0 ? (
                    getUpcomingEvents().map((event) => (
                      <ListItem 
                        key={event.id} 
                        button 
                        onClick={() => handleEventClick(event)}
                        sx={{ mb: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                      >
                        <ListItemIcon>
                          {getEventIcon(event.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={event.title}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(event.date)} • {formatTime(event.time)}
                              </Typography>
                              <Chip 
                                label={event.type} 
                                size="small" 
                                color={getEventColor(event.type)}
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary="No upcoming events"
                        secondary="Your schedule is clear!"
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Calendar Stats */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Calendar Statistics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="info.main">
                        {events.filter(e => e.type === 'google_calendar').length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Google Calendar Events
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {events.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Events
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Event Detail Dialog */}
        <Dialog 
          open={eventDialogOpen} 
          onClose={handleCloseEventDialog}
          maxWidth="sm"
          fullWidth
        >
          {selectedEvent && (
            <>
              <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                  {getEventIcon(selectedEvent.type)}
                  <Typography variant="h6">
                    {selectedEvent.title}
                  </Typography>
                </Box>
              </DialogTitle>
              <DialogContent>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Today color="action" />
                    <Typography variant="body1">
                      {formatDate(selectedEvent.date)}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={1}>
                    <AccessTime color="action" />
                    <Typography variant="body1">
                      {formatTime(selectedEvent.time)} • {selectedEvent.duration}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationOn color="action" />
                    <Typography variant="body1">
                      {selectedEvent.location}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <Description color="action" sx={{ mt: 0.5 }} />
                    <Typography variant="body1">
                      {selectedEvent.description}
                    </Typography>
                  </Box>
                  
                  <Chip 
                    label={selectedEvent.type} 
                    color={getEventColor(selectedEvent.type)}
                    sx={{ alignSelf: 'flex-start' }}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseEventDialog}>
                  Close
                </Button>
                {selectedEvent.type === 'google_calendar' && selectedEvent.html_link && (
                  <Button 
                    variant="outlined" 
                    startIcon={<CalendarMonth />}
                    onClick={() => {
                      window.open(selectedEvent.html_link, '_blank');
                    }}
                  >
                    View in Google Calendar
                  </Button>
                )}
                <Button 
                  variant="contained" 
                  startIcon={<Edit />}
                  onClick={() => {
            
                    enqueueSnackbar('Edit functionality coming soon!', { variant: 'info' });
                  }}
                >
                  Edit Event
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </>
  );
};

export default Calendar;
